import Hls from 'hls.js';

export default class Video {
	constructor(wrapper) {
		this.wrapper = wrapper;
		this.video = this.wrapper.querySelector('video');
		if (!this.video) {
			return;
		}
		this.shouldPlay = false;
		this.isPlaying = false;

		this.wrapper.addEventListener('init', () => {
			this.startHls();
		});

		this.wrapper.addEventListener('play-video', () => {
			this.shouldPlay = true;
			if (!this.hasStartedHls) {
				this.startHls();
			} else {
				this.playVideo();
			}
		});

		this.wrapper.addEventListener('pause-video', () => {
			this.pauseVideo();
		});

		this.wrapper.addEventListener('show-poster', () => {
			this.showPoster();
		});

		this.wrapper.addEventListener('mute-video', () => {
			this.video.setAttribute('muted', '');
			this.video.muted = true;
		});

		this.wrapper.addEventListener('unmute-video', () => {
			this.video.removeAttribute('muted');
			this.video.muted = false;
		});

		this.boundHandleAfterleave = this.destroy.bind(this);
		document.addEventListener('afterLeave', this.boundHandleAfterleave);
	}

	startHls() {
		const videoSrc = this.video.dataset['hlsSrc'];

		// Always use HLS.js for consistent quality control across all browsers
		if (Hls.isSupported()) {
			this.hls = new Hls({
				capLevelToPlayerSize: true,
				maxDevicePixelRatio: 2,
				startLevel: -1,
			});

			this.hls.loadSource(videoSrc);
			this.hls.attachMedia(this.video);

			const preloadSeconds = 1;
			this.hls.on(Hls.Events.BUFFER_APPENDED, () => {
				if (this.initiallyBuffered) return;
				const buffered = this.video.buffered;
				if (buffered.length > 0) {
					const bufferedSeconds = buffered.end(0) - this.video.currentTime;
					if (bufferedSeconds >= preloadSeconds) {
						this.initiallyBuffered = true;
						// Only pause buffering if the video is NOT currently playing and should NOT be playing
						if (!this.isPlaying && !this.shouldPlay) {
							this.hls.pauseBuffering();
						}
					}
				}
			});

			this.hls.on(Hls.Events.ERROR, (event, data) => {
				console.error('HLS Error:', data);
				if (data.fatal) {
					// Handle fatal errors appropriately
					this.showPoster();
				}
			});
		} else {
			// Fallback for browsers that don't support HLS.js
			console.warn('HLS.js not supported, falling back to native HLS');
			if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
				this.video.src = videoSrc;
			} else {
				console.error('Neither HLS.js nor native HLS is supported');
			}
		}
		this.hasStartedHls = true;
	}

	playVideo() {
		if (this.hls) {
			this.hls.resumeBuffering();
		}
		let startPlayPromise = this.video.play();
		if (startPlayPromise !== undefined) {
			startPlayPromise
				.then(() => {
					this.hidePoster();
					this.wrapper.classList.add('is-playing');
					this.isPlaying = true;
				})
				.catch((error) => {
					if (!this.wrapper) {
						return;
					}
					if (!this.video.muted) {
						this.wrapper.dispatchEvent(new CustomEvent('autoplay-failed', { bubbles: true }));
					}
					setTimeout(() => {
						if (!this.wrapper.classList.contains('is-playing')) {
							this.showPoster();
						}
					}, 1000);
				});
		}
	}

	showPoster() {
		this.wrapper.classList.add('show-poster');
		const poster = this.wrapper.querySelector('img');
		lazySizes.loader.unveil(poster);
	}

	hidePoster() {
		this.wrapper.classList.remove('show-poster');
	}

	pauseVideo() {
		this.video.pause();
		this.isPlaying = false;
		this.shouldPlay = false;
		if (this.hls) {
			this.hls.pauseBuffering();
		}
	}

	destroy() {
		document.removeEventListener('afterLeave', this.boundHandleAfterleave);
		if (this.hls) {
			this.hls.stopLoad();
			this.hls.detachMedia();
		}
	}
}
